<!--
  Zimobi fork of `app/javascript/dashboard/routes/dashboard/Dashboard.vue`.

  Wired in `dashboard/routes/dashboard/dashboard.routes.js`, which imports this
  file as `AppContainer` instead of the upstream one. The upstream file is left
  untouched so merges with Chatwoot stay clean; the flip side is that upstream
  changes to the dashboard shell have to be re-ported here by hand.

  Delta against upstream — keep this list in sync when you change something:

  1. `NextSidebar` comes from the Zimobi fork of the v4 sidebar
     (`custom/dashboard/components-next/sidebar/Sidebar.vue`).
  2. `BottomTabBar` is rendered after `<main>`, and the root wrapper reserves
     room for it: `flex-col ... pb-16` below `sm`, `sm:flex-row sm:pb-0` above.
     `<main>` is `flex-none ... sm:flex-1` for the same reason. The pair with the
     sidebar is what makes the mobile layer work — see the fork's own header.
  3. `hideBottomTabBar` keeps the tab bar to the `home`, `lost_lead_distribution`
     and card board routes, and drops the reserved padding elsewhere. A board has
     no way out of its own: on a phone it is reached from a conversation's card,
     and without the bar, closing that card leaves the agent stranded on it.
  4. `autoRequestPushNotificationPermissionMixin` auto-prompts for push
     permission on mobile; push is how Zimobi notifies, not the in-app inbox.
  5. Wootric NPS and Clarity are booted in production, which needs the extra
     `getAccount` / `currentUser` / `currentAccountId` getters and the
     `currentAccountName` computed.
  6. `@open-notification-panel` is wired on `NextSidebar` too. Upstream only
     wires it on the v3 sidebar, which left the notification panel unreachable
     under v4; the fork's footer bell is what emits it.
-->
<script>
import { defineAsyncComponent, ref } from 'vue';
import { mapGetters } from 'vuex';

// Zimobi fork of the v4 sidebar — see the header comment in that file.
import NextSidebar from 'custom/dashboard/components-next/sidebar/Sidebar.vue';
import WootKeyShortcutModal from 'dashboard/components/widgets/modal/WootKeyShortcutModal.vue';
import AddAccountModal from 'dashboard/components/app/AddAccountModal.vue';
import AccountSelector from 'custom/dashboard/components/account/AccountSelector.vue';
import NotificationPanel from 'custom/dashboard/components/notifications/NotificationPanel.vue';
import UpgradePage from 'dashboard/routes/dashboard/upgrade/UpgradePage.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';

import wootConstants from 'dashboard/constants/globals';

const CommandBar = defineAsyncComponent(
  () => import('dashboard/routes/dashboard/commands/commandbar.vue')
);

import CopilotLauncher from 'dashboard/components-next/copilot/CopilotLauncher.vue';
import CopilotContainer from 'dashboard/components/copilot/CopilotContainer.vue';

// Zimobi imports
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import BottomTabBar from 'dashboard/components/layout/BottomTabBar.vue';
import autoRequestPushNotificationPermissionMixin from 'dashboard/routes/dashboard/zimobi/autoRequestPushNotificationPermissionMixin';

export default {
  components: {
    NextSidebar,
    BottomTabBar,
    CommandBar,
    WootKeyShortcutModal,
    AddAccountModal,
    AccountSelector,
    NotificationPanel,
    UpgradePage,
    CopilotLauncher,
    CopilotContainer,
  },
  mixins: [autoRequestPushNotificationPermissionMixin],
  setup() {
    const upgradePageRef = ref(null);
    const { uiSettings, updateUISettings } = useUISettings();
    const { accountId } = useAccount();

    return {
      uiSettings,
      updateUISettings,
      accountId,
      upgradePageRef,
    };
  },
  data() {
    return {
      showAccountModal: false,
      showCreateAccountModal: false,
      showShortcutModal: false,
      isNotificationPanel: false,
      displayLayoutType: '',
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentUser: 'getCurrentUser',
      currentAccountId: 'getCurrentAccountId',
    }),
    currentAccountName() {
      return this.getAccount(this.currentAccountId).name;
    },
    showUpgradePage() {
      return this.upgradePageRef?.shouldShowUpgradePage;
    },
    bypassUpgradePage() {
      return [
        'billing_settings_index',
        'settings_inbox_list',
        'general_settings_index',
        'agent_list',
      ].includes(this.$route.name);
    },
    previouslyUsedSidebarView() {
      const { previously_used_sidebar_view: showSecondarySidebar } =
        this.uiSettings;
      return showSecondarySidebar;
    },
    hideBottomTabBar() {
      const routesWithBottomTabBar = [
        'home',
        'lost_lead_distribution',
        'cards_index',
        'cards_pipeline_index',
      ];
      const shouldShowBottomTabBar = routesWithBottomTabBar.includes(
        this.$route.name
      );

      return !shouldShowBottomTabBar;
    },
  },
  watch: {
    displayLayoutType() {
      const { LAYOUT_TYPES } = wootConstants;
      // Zimobi: the fork removed the layout toggle, so the layout follows the viewport alone.
      // Upstream restores the saved `previously_used_conversation_display_type`, which would
      // strand a desktop on the expanded layout with no button to leave it.
      this.updateUISettings({
        conversation_display_type:
          this.displayLayoutType === LAYOUT_TYPES.EXPANDED
            ? LAYOUT_TYPES.EXPANDED
            : LAYOUT_TYPES.CONDENSED,
        show_secondary_sidebar:
          this.displayLayoutType === LAYOUT_TYPES.EXPANDED
            ? false
            : this.previouslyUsedSidebarView,
      });
    },
  },
  mounted() {
    this.handleResize();
    window.addEventListener('resize', this.handleResize);

    if (process.env.NODE_ENV === 'production') {
      this.setupWootric();
      // eslint-disable-next-line no-undef
      clarity('identify', this.currentUser.id.toString());
    }
  },
  unmounted() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    setupWootric() {
      // TEST ONLY FLAGS - REMOVE BEFORE GOING LIVE
      // window.wootric_survey_immediately = true; // Overwrites sampling settings and forces the server to return true to all survey requests.
      // window.wootric_no_surveyed_cookie = true; // Disables the cookie writing after a survey is taken effectively disabling any client side mechanisms to prevent multiple surveys from being rendered.
      // END OF TEST ONLY FLAGS
      window.wootricSettings = {
        email: this.currentUser.email,
        account_token: 'NPS-b5872218',
        modal_theme:
          LocalStorage.get(LOCAL_STORAGE_KEYS.COLOR_SCHEME) || 'light',
        properties: {
          id: this.currentUser.id,
          nome: this.currentUser.name,
          cargo: this.currentUser.role,
          nome_imobiliaria: this.currentAccountName,
        },
      };

      // This loads the Wootric survey
      window.wootric('run');
    },
    handleResize() {
      const { SMALL_SCREEN_BREAKPOINT, LAYOUT_TYPES } = wootConstants;
      let throttled = false;
      const delay = 150;

      if (throttled) {
        return;
      }
      throttled = true;

      setTimeout(() => {
        throttled = false;
        if (window.innerWidth <= SMALL_SCREEN_BREAKPOINT) {
          this.displayLayoutType = LAYOUT_TYPES.EXPANDED;
        } else {
          this.displayLayoutType = LAYOUT_TYPES.CONDENSED;
        }
      }, delay);
    },
    openCreateAccountModal() {
      this.showAccountModal = false;
      this.showCreateAccountModal = true;
    },
    closeCreateAccountModal() {
      this.showCreateAccountModal = false;
    },
    toggleAccountModal() {
      this.showAccountModal = !this.showAccountModal;
    },
    toggleKeyShortcutModal() {
      this.showShortcutModal = true;
    },
    closeKeyShortcutModal() {
      this.showShortcutModal = false;
    },
    openNotificationPanel() {
      this.isNotificationPanel = true;
    },
    closeNotificationPanel() {
      this.isNotificationPanel = false;
    },
  },
};
</script>

<template>
  <div
    data-testid="dashboard-root"
    class="flex flex-col flex-nowrap flex-grow overflow-clip w-full max-w-full min-h-0 pb-16 ml-auto mr-auto sm:flex-row sm:flex-wrap sm:pb-0 dark:text-slate-300"
    :class="{ '!pb-0': hideBottomTabBar }"
  >
    <NextSidebar
      @open-notification-panel="openNotificationPanel"
      @toggle-account-modal="toggleAccountModal"
      @open-key-shortcut-modal="toggleKeyShortcutModal"
      @close-key-shortcut-modal="closeKeyShortcutModal"
      @show-create-account-modal="openCreateAccountModal"
    />
    <main class="flex flex-none h-full min-h-0 px-0 overflow-hidden sm:flex-1">
      <UpgradePage
        v-show="showUpgradePage"
        ref="upgradePageRef"
        :bypass-upgrade-page="bypassUpgradePage"
      />
      <template v-if="!showUpgradePage">
        <router-view />
        <CommandBar />
        <CopilotLauncher />
        <CopilotContainer />

        <NotificationPanel
          v-if="isNotificationPanel"
          @close="closeNotificationPanel"
        />
      </template>
      <AccountSelector
        :show-account-modal="showAccountModal"
        @close-account-modal="toggleAccountModal"
        @show-create-account-modal="openCreateAccountModal"
      />
      <AddAccountModal
        :show="showCreateAccountModal"
        @close-account-create-modal="closeCreateAccountModal"
      />
      <WootKeyShortcutModal
        v-model:show="showShortcutModal"
        @close="closeKeyShortcutModal"
        @clickaway="closeKeyShortcutModal"
      />
    </main>
    <BottomTabBar
      :class="{ hidden: hideBottomTabBar }"
      @toggle-accounts="toggleAccountModal"
    />
  </div>
</template>
